import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import PageShell, { SectionLabel } from "@/components/PageShell";
import SEO from "@/components/SEO";
import { getCareers } from "@/lib/api";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    getCareers()
      .then(setJobs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageShell testId="page-careers">
      <SEO
        title="Careers — Imaginepedia Technologies"
        description="Be one of us, Check open positions."
        path="/careers"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-16 md:py-24">
          <div className="md:col-span-4">
            <SectionLabel index={1}>Careers</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              Build the systems<br />
              <span className="text-zinc-500">you&apos;d want to</span><br />
              use.
            </h1>
            <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-2xl">
              We hire senior operators and future ones. Remote-first, Bengaluru-anchored. If you want to ship research, this is the place.
            </p>
          </div>
        </div>

        <div className="border-t border-white/5 pt-12 pb-24">
          {loading && (
            <p data-testid="careers-loading" className="font-mono text-sm text-zinc-500">
              Loading roles…
            </p>
          )}
          <div data-testid="careers-list" className="divide-y divide-white/5 border-y border-white/5">
            {jobs.map((j, i) => {
              const isOpen = openId === j.id;
              return (
                <motion.div
                  key={j.id}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  <button
                    onClick={() => setOpenId(isOpen ? null : j.id)}
                    data-testid={`career-toggle-${i}`}
                    className="w-full grid grid-cols-12 gap-4 py-8 items-center text-left hover:bg-white/[0.02] transition-colors duration-200 px-2"
                  >
                    <span className="col-span-1 font-mono text-xs text-zinc-500">0{i + 1}</span>
                    <span className="col-span-11 md:col-span-6 font-display text-xl md:text-2xl text-white">
                      {j.title}
                    </span>
                    <span className="col-span-6 md:col-span-2 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                      {j.location}
                    </span>
                    <span className="col-span-4 md:col-span-2 font-mono text-[11px] uppercase tracking-[0.15em] text-[#CCFF00]">
                      {j.type}
                    </span>
                    <span className="col-span-2 md:col-span-1 flex justify-end">
                      {isOpen ? <Minus size={18} className="text-[#CCFF00]" /> : <Plus size={18} className="text-zinc-500" />}
                    </span>
                  </button>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="grid grid-cols-12 gap-4 pb-8 px-2"
                    >
                      <div className="col-span-12 md:col-span-8 md:col-start-2">
                        <p className="text-zinc-400 leading-relaxed">{j.description}</p>
                        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                          Dept: {j.department}
                        </p>
                        <Link
                          to="/contact"
                          data-testid={`career-apply-${i}`}
                          className="mt-6 inline-block bg-[#CCFF00] text-black px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white transition-colors duration-200"
                        >
                          Apply →
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>

          <div className="mt-16 bg-[#0C0C0D] border border-white/5 p-8 md:p-12">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
              Don&apos;t see your role?
            </p>
            <h3 className="font-display text-2xl md:text-3xl text-white">
              We always talk to unusually good people.
            </h3>
            <Link
              to="/contact"
              data-testid="careers-open-cta"
              className="mt-6 inline-block border border-white/10 hover:border-[#CCFF00]/50 hover:text-[#CCFF00] text-white px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200"
            >
              Send us a note
            </Link>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
